import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAsyncDebounce } from 'react-table'
import { FormikErrors, useFormikContext } from 'formik'
import { mdiAlert, mdiCheck, mdiInformationOutline } from '@mdi/js'

import { checkEmailThunk } from 'features/team/slice'
import { useAppDispatch } from 'app/hooks'
import { CheckboxField, EmailField, TextField } from 'components/Fields'
import { teamAddUserModalMessages } from 'features/team/components/AddUserModal/messages'
import { commonMessages } from 'app/i18n/messages'
import { AddUserModalFormValues, MappedDomainUser } from 'features/team/types'
import { SpinnerCentered } from 'components/Loader'
import {
  AddUserModalFormContainer,
  AddUserModalCancelButton,
  AddUserModalFormBody,
  AddUserModalFormButtonsWrapper,
  AddUserModalInfo,
  AddUserModalPermissionsContainer,
  AddUserModalPermissionsTitle,
  AddUserModalSubmitButton,
  AddUserModalUnsuccessInfo,
  AddUserModalSuccessInfo,
  AddUserModalInfoIcon,
  AddUserModalSuccessInfoIcon,
  AddUserModalUnsuccessInfoIcon,
  AddUserModalFormEmailBlock,
  AddUserModalFieldWrapper,
  AddUserModalSpinnerWrapper,
} from './styles'
import Tooltip from 'components/Tooltip'

interface AddUserModalFormProps {
  setIsAddUserModalOpen: Dispatch<SetStateAction<boolean>>
  domainUsersList: MappedDomainUser[]
  hasDomainNesting: boolean
}

function AddUserModalForm({
  setIsAddUserModalOpen,
  domainUsersList,
  hasDomainNesting,
}: AddUserModalFormProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const [isEmailChecking, setIsEmailChecking] = useState(false)

  const {
    values,
    errors,
    status,
    validateForm,
    isValid,
    setStatus,
    isSubmitting,
    resetForm,
  } = useFormikContext<AddUserModalFormValues>()

  const showNameFields =
    status.isNewUser &&
    !status.isUserAlreadyAdded &&
    !errors.email &&
    !isEmailChecking
  const showAddUserModalInfo =
    !errors.email && !isEmailChecking && !status.isError
  const submitDisabled =
    isSubmitting || !isValid || isEmailChecking || status.isUserAlreadyAdded

  const handleEmailInputChange = useAsyncDebounce(
    async (
      email: string,
      validateForm: () => Promise<FormikErrors<AddUserModalFormValues>>,
      setStatus: (status?: any) => void
    ) => {
      setStatus({ isError: false })
      const errors = await validateForm()
      if (!errors.email) {
        try {
          const userAlreadyAdded = domainUsersList.find(
            (user) => user.email === email
          )
          if (!!userAlreadyAdded) {
            return setStatus({ isUserAlreadyAdded: true })
          }

          const isNewUser = await dispatch(checkEmailThunk({ email }))
          setStatus({ isNewUser: !!isNewUser })
        } catch (err) {
          setStatus({ isError: true })
          console.log(`Error checking the availability of email ${email}`, err)
        }
      }
    },
    300
  )

  useEffect(() => {
    setIsEmailChecking(true)
    handleEmailInputChange(values.email, validateForm, setStatus).then(() =>
      setIsEmailChecking(false)
    )
  }, [validateForm, values.email, handleEmailInputChange, setStatus])

  return (
    <AddUserModalFormContainer>
      <AddUserModalFormBody>
        <AddUserModalFormEmailBlock>
          <AddUserModalFieldWrapper>
            <EmailField
              name="email"
              autoComplete="email"
              placeholder={formatMessage(teamAddUserModalMessages.emailField)}
              showLabel
            />
          </AddUserModalFieldWrapper>
          {showAddUserModalInfo && (
            <>
              {status.isNewUser && !status.isUserAlreadyAdded && (
                <AddUserModalInfo>
                  <AddUserModalInfoIcon path={mdiInformationOutline} />
                  {formatMessage(teamAddUserModalMessages.newUserInfoMessage)}
                </AddUserModalInfo>
              )}
              {!status.isNewUser && !status.isUserAlreadyAdded && (
                <AddUserModalSuccessInfo>
                  <AddUserModalSuccessInfoIcon path={mdiCheck} />
                  {formatMessage(teamAddUserModalMessages.userFoundInfoMessage)}
                </AddUserModalSuccessInfo>
              )}
              {status.isUserAlreadyAdded && (
                <AddUserModalUnsuccessInfo>
                  <AddUserModalUnsuccessInfoIcon path={mdiAlert} />
                  {formatMessage(
                    teamAddUserModalMessages.userAlreadyInTeamInfoMessage
                  )}
                </AddUserModalUnsuccessInfo>
              )}
            </>
          )}

          {!errors.email && isEmailChecking && (
            <AddUserModalSpinnerWrapper>
              <SpinnerCentered size="32" />
            </AddUserModalSpinnerWrapper>
          )}

          {status.isError && (
            <AddUserModalUnsuccessInfo>
              <AddUserModalUnsuccessInfoIcon path={mdiAlert} />
              {formatMessage(commonMessages.commonError)}
            </AddUserModalUnsuccessInfo>
          )}
        </AddUserModalFormEmailBlock>

        {showNameFields && (
          <>
            <AddUserModalFieldWrapper>
              <TextField
                name="firstName"
                placeholder={formatMessage(
                  teamAddUserModalMessages.firstNameField
                )}
                disabled={!status.isNewUser}
                showLabel
              />
            </AddUserModalFieldWrapper>
            <AddUserModalFieldWrapper>
              <TextField
                name="lastName"
                placeholder={formatMessage(
                  teamAddUserModalMessages.lastNameField
                )}
                disabled={!status.isNewUser}
                showLabel
              />
            </AddUserModalFieldWrapper>
          </>
        )}

        <AddUserModalPermissionsContainer>
          <AddUserModalPermissionsTitle>
            {formatMessage(teamAddUserModalMessages.permissionsBlockTitle)}
          </AddUserModalPermissionsTitle>
          <CheckboxField
            name="admin"
            disabled={status.isUserAlreadyAdded}
            labelText={formatMessage(
              teamAddUserModalMessages.adminPermissionLabel
            )}
          />
          {hasDomainNesting && (
            <CheckboxField
              name="config"
              disabled={status.isUserAlreadyAdded}
              labelText={formatMessage(
                teamAddUserModalMessages.configPermissionLabel
              )}
            />
          )}
          {/* TODO: make disable false and tooltip conditionally when backend support of subscriptionManagement field will be implemented */}
          <Tooltip
            text="Subscription Management permissions can only be changed in the Savant Community."
            showOn="click"
          >
            <CheckboxField
              name="subscriptionManagement"
              disabled={true}
              labelText={formatMessage(
                teamAddUserModalMessages.subscriptionManagementPermissionLabel
              )}
            />
          </Tooltip>
        </AddUserModalPermissionsContainer>
      </AddUserModalFormBody>

      <AddUserModalFormButtonsWrapper>
        <AddUserModalCancelButton
          variant="secondary"
          type="button"
          onClick={() => {
            resetForm()
            setIsAddUserModalOpen(false)
          }}
        >
          {formatMessage(commonMessages.cancel)}
        </AddUserModalCancelButton>

        {isSubmitting ? (
          <AddUserModalSpinnerWrapper>
            <SpinnerCentered size="32" />
          </AddUserModalSpinnerWrapper>
        ) : (
          <AddUserModalSubmitButton
            variant="primary"
            type="submit"
            disabled={submitDisabled}
          >
            {status.isNewUser
              ? formatMessage(teamAddUserModalMessages.sendInviteButton)
              : formatMessage(teamAddUserModalMessages.addUserButton)}
          </AddUserModalSubmitButton>
        )}
      </AddUserModalFormButtonsWrapper>
    </AddUserModalFormContainer>
  )
}

export default AddUserModalForm
